import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTaskSource,
  extractIntentContractDraft,
  getFeaturePlan,
  getIntentContractVersion,
  getIntentContractVersionReadiness,
  applyTaskProductProposal,
  approveTaskProductDocument,
  createTaskProductProposal,
  patchTaskProductDocument,
  productApproveIntentContract,
  rejectTaskProductProposal,
  resolveIntentContractQuestion,
  undoTaskProductDocument,
  uploadTaskSourceContent,
} from "./api";
import api from "./api";
import {
  BUILDER_STAGE_COPY,
  classifyFounderUtterance,
  currentFounderQuestion,
  missingSummary,
  questionChoicesForMessage,
  selectNextConversationQuestion,
  TASK_BUILDER_SUGGESTIONS,
  TASK_SECTION_LABELS,
  taskSoFarFromDraft,
  understandingFromRequest,
  whyThisQuestionMatters,
} from "./taskBuilderModel";
import { founderGlance } from "./founderExperience";
import { conversationStateLabel, proposalPreview } from "./taskHandoffDocument";
import { findSimilarActiveTask, taskShortTitle } from "./taskShortTitle";
import { TASK_LABEL, TASKS_LABEL } from "./taskCopy";
import { answerTaskChat, classifyTaskChatUtterance } from "./taskConversationModel";
import { TASK_SOURCE_ACCEPT } from "./taskSourceCopy";
import "./TaskBuilder.css";

function uid() {
  return `m-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function TaskBuilder({
  repo,
  plans = [],
  plan = null,
  mutateAllowed = true,
  onCreated,
  onSent,
  onCancel,
  onOpenTask,
  onTaskMutated,
  onCreateTask = null,
  onSimilarTask = null,
  forceCreate = false,
  discussContext = null,
  frozen = false,
  saveState = "Saved",
  embedded = false,
  hidePreview = false,
}) {
  const [text, setText] = useState("");
  const [messages, setMessages] = useState(() => [
    {
      id: "greet",
      role: "ai",
      text: "Tell me what you want to build. It does not need to be perfectly written.",
    },
  ]);
  const [stage, setStage] = useState(plan ? "understanding" : "understanding");
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState("");
  const [similar, setSimilar] = useState(null);
  const [draft, setDraft] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [localPlan, setLocalPlan] = useState(plan);
  const sendingRef = useRef(false);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);
  const createKeyRef = useRef(null);
  const [reviewing, setReviewing] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [frozenPrompt, setFrozenPrompt] = useState(false);
  const requestText = localPlan?.featureRequestText || "";

  const push = useCallback((message) => {
    setMessages((prev) => [...prev, { id: uid(), ...message }]);
  }, []);

  const pushQuestion = useCallback((question) => {
    if (!question) return;
    const questionId = question.questionId || null;
    const text = question.displayText || question.text || "";
    setMessages((prev) => {
      const already = prev.some(
        (item) =>
          item.kind === "question" &&
          ((questionId && item.questionId === questionId) ||
            (!questionId && item.text === text))
      );
      if (already) return prev;
      return [
        ...prev,
        {
          id: uid(),
          role: "ai",
          kind: "question",
          questionId,
          text,
          why: whyThisQuestionMatters(question),
        },
      ];
    });
    setStage("clarifying");
  }, []);

  useEffect(() => {
    scrollerRef.current?.scrollTo?.({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    if (!discussContext?.section || !localPlan?._id) return;
    const label = TASK_SECTION_LABELS[discussContext.section] || "this part of the Task";
    push({
      role: "ai",
      kind: "context",
      text: discussContext.itemId
        ? `You’re editing: ${label}. What should change about this item?`
        : `You’re editing: ${label}. What would you like to change?`,
    });
  }, [discussContext?.section, discussContext?.itemId, localPlan?._id, push]);

  const preview = useMemo(
    () => taskSoFarFromDraft(draft, requestText, localPlan),
    [draft, requestText, localPlan]
  );
  const glance = useMemo(() => founderGlance(requestText), [requestText]);
  const activeQuestionId = useMemo(
    () => [...messages].reverse().find((item) => item.kind === "question")?.id,
    [messages]
  );
  const storageKey = `task-builder:${repo?._id || "repo"}:${localPlan?._id || "new"}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (Array.isArray(saved.messages) && saved.messages.length) {
        const seen = new Set();
        const deduped = [];
        for (const item of saved.messages) {
          if (item?.kind === "question") {
            const key = item.questionId || item.text;
            if (key && seen.has(key)) continue;
            if (key) seen.add(key);
          }
          deduped.push(item);
        }
        setMessages(deduped);
      }
      if (saved.stage) setStage(saved.stage);
      if (typeof saved.text === "string") setText(saved.text);
    } catch {
      /* ignore */
    }
    // Restore once per Task identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ messages, stage, text }));
    } catch {
      /* ignore */
    }
  }, [messages, stage, text, storageKey]);

  const loadDraft = useCallback(
    async (featurePlan, request, { announce = true, signal } = {}) => {
      if (!repo?._id) return;
      setPreparing(true);
      if (announce) setStage("understanding");
      try {
        const result = await extractIntentContractDraft({
          repoId: repo._id,
          productRequestText: request || featurePlan.featureRequestText,
          featurePlanId: featurePlan._id,
          persist: true,
        });
        if (signal?.aborted) return;
        const nextDraft = result?.draft || null;
        const nextVersion = result?.persisted?.versionId || null;
        if (nextVersion) {
          const loaded = await getIntentContractVersion(nextVersion);
          if (signal?.aborted) return;
          setDraft(loaded?.content || loaded || nextDraft);
          setVersionId(nextVersion);
          try {
            await getIntentContractVersionReadiness(nextVersion, "strict");
          } catch {
            /* optional */
          }
        } else {
          setDraft(nextDraft);
        }
        if (!announce || signal?.aborted) return;
        const question = selectNextConversationQuestion(nextDraft || result?.draft, request || featurePlan.featureRequestText);
        if (question.next) {
          pushQuestion(question.next);
        } else {
          setStage("ready");
          setMessages((prev) => {
            if (prev.some((item) => item.kind === "ready")) return prev;
            return [
              ...prev,
              {
                id: uid(),
                role: "ai",
                kind: "ready",
                text: "I think this Task is ready for your review.",
              },
            ];
          });
        }
      } catch (err) {
        if (signal?.aborted) return;
        setError(err?.message || "I could not prepare this Task yet.");
      } finally {
        if (!signal?.aborted) setPreparing(false);
      }
    },
    [pushQuestion, repo?._id]
  );

  useEffect(() => {
    if (!plan?._id || (localPlan?._id === plan._id && draft)) return undefined;
    setLocalPlan(plan);
    if (plan.status !== "completed" && plan.status !== "insufficient_evidence") return undefined;
    let savedMessages = [];
    try {
      const saved = sessionStorage.getItem(`task-builder:${repo?._id || "repo"}:${plan._id}`);
      savedMessages = saved ? JSON.parse(saved).messages || [] : [];
    } catch {
      savedMessages = [];
    }
    const alreadyTalking =
      Array.isArray(savedMessages) &&
      (savedMessages.length > 1 || savedMessages.some((item) => item?.kind === "question" || item?.kind === "ready"));
    const controller = new AbortController();
    void loadDraft(plan, plan.featureRequestText, {
      announce: !alreadyTalking,
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [plan?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const waitForPlan = async (created) => {
    let current = created;
    for (let i = 0; i < 40; i += 1) {
      const status = String(current.status || "").toLowerCase();
      if (["completed", "failed", "insufficient_evidence"].includes(status)) return current;
      current = await getFeaturePlan(created._id);
      setLocalPlan(current);
      const nextStatus = String(current.status || "").toLowerCase();
      if (["completed", "failed", "insufficient_evidence"].includes(nextStatus)) return current;
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return current;
  };

  const createPlan = async (request, { forceDuplicate } = {}) => {
    if (sendingRef.current) return;
    const similarTask = findSimilarActiveTask(plans, request);
    if (similarTask && !forceDuplicate) {
      setSimilar(similarTask);
      if (typeof onSimilarTask === "function") onSimilarTask(similarTask);
      return;
    }
    sendingRef.current = true;
    setBusy(true);
    setError("");
    setSimilar(null);
    if (typeof onSimilarTask === "function") onSimilarTask(null);
    setStage("understanding");
    if (!createKeyRef.current) {
      createKeyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `task-create-${Date.now()}`;
    }
    try {
      let created;
      if (typeof onCreateTask === "function") {
        created = await onCreateTask({
          featureRequestText: request,
          sourceArtifactIds: artifacts.map((item) => item.artifactId),
          forceDuplicate: Boolean(forceDuplicate),
          selectAfterCreate: false,
        });
        if (!created?._id) {
          return;
        }
      } else {
        const response = await api.post("/feature-plans", {
          repoId: repo._id,
          featureRequestText: request,
          sourceArtifactIds: artifacts.map((item) => item.artifactId),
          idempotencyKey: createKeyRef.current,
          originKind: "user",
        });
        created = response.data.plan || response.data;
      }
      setLocalPlan(created);
      if (typeof onCreated === "function") onCreated(created);
      const ready = await waitForPlan(created);
      if (String(ready.status).toLowerCase() === "failed") {
        setError("I could not prepare this Task. Try again in a moment.");
        return;
      }
      await loadDraft(ready, request);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "This Task could not be started.");
    } finally {
      sendingRef.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!forceCreate) return undefined;
    const request = String(text || requestText || "").trim();
    if (!request) return undefined;
    void createPlan(request, { forceDuplicate: true });
    return undefined;
    // Retry only when the parent flips Create anyway. createPlan is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCreate]);

  const answerQuestion = async (action, answerText) => {
    const question = currentFounderQuestion(draft, requestText).next;
    if (!versionId || !question?.questionId) {
      push({
        role: "ai",
        text: "I don't have a planning question queued yet. Ask what this Task is about, or wait a moment while I finish preparing it.",
      });
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload =
        action === "answer"
          ? { action: "answer", answerText }
          : action === "not_required"
            ? { action: "mark_not_required", reason: answerText || "This is not relevant." }
            : { action: "defer", note: answerText || "Deferred." };
      const data = await resolveIntentContractQuestion(versionId, question.questionId, payload);
      const nextDraft = data?.content || data?.version?.content || draft;
      setDraft(nextDraft);
      if (data?.persisted?.versionId || data?.versionId) {
        setVersionId(data.persisted?.versionId || data.versionId);
      }
      const next = selectNextConversationQuestion(nextDraft, requestText);
      if (next.next && next.next.questionId !== question.questionId) {
        pushQuestion(next.next);
      } else {
        setStage("ready");
        push({
          role: "ai",
          kind: "ready",
          text: "I think this Task is ready for your review.\n\nOutcome and what engineering will build are on the Full Task.\nImportant product decisions, what is not included, and how success will be checked are there too.",
        });
      }
    } catch (err) {
      setError(err?.message || "I could not save that answer.");
    } finally {
      setBusy(false);
    }
  };

  const proposeFromUtterance = async (utterance, extra = {}) => {
    if (!localPlan?._id) return false;
    if (frozen) {
      setFrozenPrompt(true);
      push({
        role: "ai",
        text: "This Task has already been sent to engineering. Changing it will create a new version for review.",
      });
      return true;
    }
    try {
      const data = await createTaskProductProposal(localPlan._id, {
        utterance,
        section: extra.section || discussContext?.section,
        itemId: extra.itemId || discussContext?.itemId,
        idempotencyKey: `${localPlan._id}:${String(utterance).toLowerCase().slice(0, 80)}:${Date.now()}`,
      });
      const item = data.proposal;
      if (item?.alreadyPresent) {
        push({
          role: "ai",
          kind: "update",
          section: item.targetSection,
          text: `Updated the Task\n• Already on the Task`,
        });
        return true;
      }
      if (item && (data.replayed || item.status === "applied") && item.confirmationRequired === false) {
        const summary = (item.downstreamImplications || [])[0] || item.why || "Saved";
        push({
          role: "ai",
          kind: "update",
          section: item.targetSection,
          text: `Updated the Task\n• ${summary}`,
        });
        if (typeof onTaskMutated === "function") onTaskMutated(data, item.targetSection);
        return true;
      }
      if (item && item.confirmationRequired === false && (!item.status || item.status === "pending")) {
        await applyCurrentProposal(item);
        return true;
      }
      setProposal(item);
      push({
        role: "ai",
        kind: "proposal",
        text: "Here’s the change I would make",
        proposal: item,
      });
      return true;
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "FROZEN") {
        setFrozenPrompt(true);
        push({
          role: "ai",
          text: "This Task has already been sent to engineering. Changing it will create a new version for review.",
        });
        return true;
      }
      setError(err?.response?.data?.error || err?.message || "I could not update the Task from that message.");
      push({
        role: "ai",
        text: "I understood that as a change request, but I need a clearer instruction to update the Task safely.",
      });
      return true;
    }
  };

  const send = async (raw) => {
    const value = String(raw ?? text).trim();
    if (!value || busy) return;
    if (frozen && classifyFounderUtterance(value).kind !== "why" && classifyTaskChatUtterance(value) !== "question") {
      setText("");
      push({ role: "founder", text: value });
      setFrozenPrompt(true);
      push({
        role: "ai",
        text: "This Task has already been sent to engineering. Changing it will create a new version for review.",
      });
      return;
    }
    if (!mutateAllowed) {
      setText("");
      push({ role: "founder", text: value });
      push({
        role: "ai",
        text: "This workspace is read-only. You can still ask what the Task is about, but I can't save answers.",
      });
      return;
    }
    setText("");
    push({ role: "founder", text: value });
    if (!localPlan?._id) {
      await createPlan(value);
      return;
    }
    const classified = classifyFounderUtterance(value);
    if (classified.kind === "answer" && classifyTaskChatUtterance(value) === "question") {
      const reply = answerTaskChat(value, {
        plan: localPlan,
        projection: {
          stage: "Planning",
          whatHappensNext: "Answer the next planning question so we can send this to engineering.",
        },
      });
      push({ role: "ai", text: reply.text || `I can answer from this ${TASK_LABEL}.` });
      return;
    }
    if (classified.kind === "why") {
      const question = currentFounderQuestion(draft, requestText).next;
      push({ role: "ai", text: whyThisQuestionMatters(question) });
      return;
    }
    if (classified.kind === "recommend") {
      push({
        role: "ai",
        text: "I recommend the smallest option that still matches your outcome. You can still change it.",
      });
      return;
    }
    if (classified.kind === "show_task") {
      if (typeof onOpenTask === "function") onOpenTask();
      else setPreviewOpen(true);
      push({ role: "ai", text: "Open Task to review exactly what engineering will receive." });
      return;
    }
    if (classified.kind === "sources") {
      push({
        role: "ai",
        text: artifacts.length
          ? `This is based on your request and ${artifacts.length} attached source${artifacts.length === 1 ? "" : "s"}. Open Task to see Sources.`
          : "This is based on your request and any repository behavior I already have. Open Task to see Sources.",
      });
      return;
    }
    if (classified.kind === "defer") {
      await answerQuestion("defer", value);
      return;
    }
    if (classified.kind === "unsure") {
      const question = currentFounderQuestion(draft, requestText).next;
      push({
        role: "ai",
        kind: "recommend",
        text: question
          ? `I recommend the smallest option that still matches your outcome.\n\nTradeoff: ${whyThisQuestionMatters(question)}\n\nYou can accept that or type a different answer.`
          : "I recommend keeping the smallest change that still matches your outcome. Tradeoff: a broader change would expand the work. You can accept that or type a different answer.",
      });
      return;
    }
    if (classified.kind === "ready") {
      setStage("ready");
      push({ role: "ai", kind: "ready", text: "I think this Task is ready for your review." });
      return;
    }
    if (classified.kind === "missing") {
      push({ role: "ai", text: missingSummary(draft, requestText) });
      return;
    }
    if (classified.kind === "show_changed") {
      if (typeof onOpenTask === "function") onOpenTask(proposal?.targetSection || "requirements");
      push({ role: "ai", text: "Open Task to see the latest structured change." });
      return;
    }
    if (classified.kind === "mutate" || classified.kind === "revise") {
      const proposed = await proposeFromUtterance(value, { section: classified.section });
      if (proposed) return;
    }
    if (classified.kind === "not_relevant") {
      await answerQuestion("not_required", value);
      return;
    }
    if (classified.kind === "revise") {
      const proposed = await proposeFromUtterance(value);
      if (proposed) return;
      await answerQuestion("answer", value);
      return;
    }
    await answerQuestion("answer", value);
  };

  const sendToEngineering = async () => {
    if (!localPlan?._id && !versionId) return;
    setBusy(true);
    try {
      if (localPlan?._id) {
        await approveTaskProductDocument(localPlan._id);
      } else if (versionId) {
        await productApproveIntentContract(versionId, { policy: "strict" });
      }
      setStage("sent");
      setReviewing(false);
      push({
        role: "ai",
        kind: "sent",
        text: "Task sent to engineering. Engineering will review the plan and either accept it or ask for clarification.",
      });
      if (typeof onSent === "function") onSent(localPlan);
    } catch (err) {
      setError(
        err?.message ||
          "This Task is not ready to send yet. Keep talking, or review the open decisions."
      );
    } finally {
      setBusy(false);
    }
  };

  const attachFiles = async (files) => {
    if (!repo?._id) return;
    for (const file of Array.from(files || [])) {
      try {
        const created = await createTaskSource({ repoId: repo._id, originalFilename: file.name });
        const bytes = await file.arrayBuffer();
        const stored = await uploadTaskSourceContent({
          repoId: repo._id,
          artifactId: created.artifactId,
          bytes,
        });
        setArtifacts((prev) => [...prev, stored]);
        push({ role: "system", text: `Added ${file.name}` });
      } catch (err) {
        setError(err?.message || "That screenshot could not be added.");
      }
    }
  };

  const applyCurrentProposal = async (item) => {
    if (!localPlan?._id || !item?.proposalId) return;
    const data = await applyTaskProductProposal(localPlan._id, item.proposalId);
    setProposal(null);
    const summary = (item.downstreamImplications || [])[0] || item.why || "Saved";
    push({
      role: "system",
      kind: "update",
      section: item.targetSection,
      text: `Updated the Task\n• ${summary}`,
    });
    if (typeof onTaskMutated === "function") onTaskMutated(data, item.targetSection);
    return data;
  };

  return (
    <div className={`task-builder${embedded ? " is-embedded" : ""}`} data-testid="task-builder">
      <header className="task-builder-top" data-testid="conversation-header">
        <div>
          <p className="task-builder-kicker">{repo?.owner}/{repo?.name}</p>
          <h2>{localPlan ? taskShortTitle(requestText) || `Untitled ${TASK_LABEL}` : "What do you want to build?"}</h2>
          <p data-testid="conversation-state">{conversationStateLabel(stage, frozen)}</p>
          <p className="task-builder-save" data-testid="conversation-save-state">{saveState}</p>
          {discussContext?.section ? (
            <p data-testid="conversation-context">
              You’re editing: {TASK_SECTION_LABELS[discussContext.section] || discussContext.section}.
            </p>
          ) : !localPlan ? (
            <p>Talk through the idea with AI. It does not need to be perfectly written. You can add screenshots or documents if they help.</p>
          ) : (
            <p>Talk through the idea with AI. Open Task to review and edit exactly what engineering will receive.</p>
          )}
        </div>
        <div>
          {typeof onOpenTask === "function" && localPlan?._id ? (
            <button type="button" className="task-builder-primary" data-testid="conversation-open-task" onClick={() => onOpenTask()}>
              View Full Task
            </button>
          ) : null}
          {typeof onCancel === "function" && !embedded ? (
            <button type="button" className="task-builder-back" onClick={onCancel}>
              Back to {TASKS_LABEL}
            </button>
          ) : null}
        </div>
      </header>

      <div className={`task-builder-layout${hidePreview ? " is-chat-only" : ""}`}>
        <section className="task-builder-chat" aria-label={`${TASK_LABEL} conversation`}>
          <p className="task-builder-stage" data-testid="task-builder-stage">
            {localPlan ? "Conversation" : BUILDER_STAGE_COPY[stage] || BUILDER_STAGE_COPY.understanding}
          </p>
          <div className="task-builder-log" ref={scrollerRef} data-testid="task-builder-log">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`task-builder-msg task-builder-msg-${message.role}`}
                data-kind={message.kind || "text"}
              >
                <p className="task-builder-speaker">{message.role === "founder" ? "You" : message.role === "system" ? "Task" : "RenderRepo"}</p>
                <p>{message.text}</p>
                {message.kind === "question" && message.id === activeQuestionId ? (
                  <div>
                    {message.why ? <p className="task-builder-why">Why this matters: {message.why}</p> : null}
                    <div className="task-builder-choices">
                      {questionChoicesForMessage(message).map((choice) => (
                        <button
                          key={choice.id}
                          type="button"
                          onClick={() => send(choice.label === "Use your recommendation" ? "What would you recommend?" : choice.label)}
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {message.kind === "proposal" && message.proposal ? (
                  <div className="task-builder-proposal" data-testid="conversation-proposal">
                    <p><strong>Before</strong> {proposalPreview(message.proposal).before}</p>
                    <p><strong>After</strong> {proposalPreview(message.proposal).after}</p>
                    <p>Why: {proposalPreview(message.proposal).why}</p>
                    {(message.proposal.downstreamImplications || []).length ? (
                      <p>Sections affected: {(message.proposal.downstreamImplications || []).join("; ")}</p>
                    ) : null}
                    <div className="task-builder-choices">
                      <button type="button" className="task-builder-primary" onClick={() => void applyCurrentProposal(message.proposal)}>
                        Apply change
                      </button>
                      <button type="button" onClick={() => setText(message.proposal.utterance || "")}>
                        Edit proposal
                      </button>
                      <button type="button" onClick={() => void rejectTaskProductProposal(localPlan._id, message.proposal.proposalId)}>
                        Keep Task as-is
                      </button>
                    </div>
                  </div>
                ) : null}
                {message.kind === "update" ? (
                  <div className="task-builder-choices">
                    <button type="button" onClick={() => void undoTaskProductDocument(localPlan._id).then(() => onTaskMutated?.())}>
                      Undo
                    </button>
                    {typeof onOpenTask === "function" ? (
                      <button type="button" data-testid="conversation-view-in-task" onClick={() => onOpenTask(message.section)}>
                        View in Task
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {message.kind === "ready" && stage !== "sent" ? (
                  <div className="task-builder-choices">
                    <button
                      type="button"
                      className="task-builder-primary"
                      onClick={() => (typeof onOpenTask === "function" ? onOpenTask() : setReviewing(true))}
                    >
                      View Full Task
                    </button>
                    <button type="button" onClick={() => setReviewing(false)}>
                      Keep talking
                    </button>
                  </div>
                ) : null}
                {message.kind === "sent" ? (
                  <div className="task-builder-choices">
                    <p>Waiting on engineering.</p>
                    {typeof onCancel === "function" ? (
                      <button type="button" onClick={onCancel}>
                        Return to {TASKS_LABEL}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
            {busy || preparing ? <p className="task-builder-busy">{busy ? "Working…" : "Preparing the next question…"}</p> : null}
          </div>

          {similar && typeof onSimilarTask !== "function" ? (
            <div className="task-builder-similar" data-testid="similar-task-warning">
              <p>A similar Task already exists.</p>
              <button type="button" onClick={() => createPlan(text || requestText, { forceDuplicate: true })}>
                Create separate Task anyway
              </button>
            </div>
          ) : null}
          {error ? <p className="error-message" role="alert">{error}</p> : null}
          {frozenPrompt ? (
            <div className="task-builder-similar" data-testid="conversation-frozen">
              <p>This Task has already been sent to engineering. Changing it will create a new version for review.</p>
              <button
                type="button"
                onClick={async () => {
                  if (localPlan?._id) {
                    try {
                      await patchTaskProductDocument(localPlan._id, { createSuccessor: true, content: {} });
                    } catch (err) {
                      setError(err?.response?.data?.error || "A new version could not be started.");
                      return;
                    }
                  }
                  setFrozenPrompt(false);
                  push({ role: "ai", text: "A new draft version is ready. Tell me what should change." });
                }}
              >
                Propose a revision
              </button>
              <button type="button" onClick={() => setFrozenPrompt(false)}>Ask a question without changing the Task</button>
              <button type="button" onClick={() => setFrozenPrompt(false)}>Cancel</button>
            </div>
          ) : null}

          {reviewing && stage !== "sent" ? (
            <section className="task-builder-review-doc" data-testid="task-review-document">
              <h3>Review Task</h3>
              <h4>Outcome</h4>
              <p>{glance.outcome}</p>
              <h4>Who this helps</h4>
              <p>{understandingFromRequest(requestText).people}</p>
              <h4>Requirements</h4>
              <ul>
                {(glance.inScope || []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h4>Not included</h4>
              <ul>
                {(glance.notIncluded || []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h4>How success will be tested</h4>
              <p>{glance.success}</p>
              <div className="task-builder-choices">
                <button type="button" className="task-builder-primary" onClick={sendToEngineering}>
                  Send to engineering
                </button>
                <button type="button" onClick={() => setReviewing(false)}>
                  Keep refining
                </button>
                <button type="button" onClick={() => setReviewing(false)}>
                  Discuss with AI
                </button>
              </div>
            </section>
          ) : null}

          <form
            className={`task-builder-composer${dropActive ? " is-drop" : ""}`}
            onSubmit={(event) => {
              event.preventDefault();
              void send(text);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDropActive(true);
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDropActive(false);
              void attachFiles(event.dataTransfer?.files);
            }}
          >
            {!localPlan ? (
              <div className="task-builder-suggestions">
                {TASK_BUILDER_SUGGESTIONS.map((item) => (
                  <button key={item} type="button" onClick={() => setText(item)}>
                    {item}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="visually-hidden" htmlFor="task-builder-input">
              {localPlan ? "Message to AI" : "What do you want to build?"}
            </label>
            <textarea
              id="task-builder-input"
              data-testid="task-builder-input"
              aria-label={localPlan ? "Message to AI" : "What do you want to build?"}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onPaste={(event) => {
                const files = Array.from(event.clipboardData?.files || []);
                if (files.some((file) => file.type.startsWith("image/"))) {
                  event.preventDefault();
                  void attachFiles(files);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void send(text);
                }
              }}
              placeholder={localPlan ? "Answer, ask why, or add a detail" : "Describe the work in your own words"}
              rows={3}
              disabled={busy || !mutateAllowed}
            />
            <div className="task-builder-tools">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}>
                Attach
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                Screenshot
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={TASK_SOURCE_ACCEPT}
                hidden
                multiple
                onChange={(event) => {
                  void attachFiles(event.target.files);
                  event.target.value = "";
                }}
              />
              <button type="submit" className="task-builder-primary" disabled={busy || !text.trim() || !mutateAllowed}>
                Send
              </button>
            </div>
          </form>
        </section>

        {hidePreview ? null : (
        <aside className={`task-builder-preview${previewOpen ? " is-open" : ""}`} data-testid="task-so-far">
          <button type="button" className="task-builder-preview-toggle" onClick={() => setPreviewOpen((open) => !open)}>
            Task so far
          </button>
          <div className="task-builder-preview-body">
            <h3>Outcome</h3>
            <p>{preview.outcome}</p>
            <h3>Scope</h3>
            <ul>
              {preview.scope.length ? preview.scope.map((item) => (
                <li key={item.text}>{item.text} <span>{item.marker}</span></li>
              )) : <li>Nothing confirmed yet.</li>}
            </ul>
            <h3>Not included</h3>
            <ul>
              {preview.notIncluded.length ? preview.notIncluded.map((item) => (
                <li key={item.text}>{item.text}</li>
              )) : <li>Nothing excluded yet.</li>}
            </ul>
            <h3>Decisions</h3>
            <ul>
              {preview.decisions?.length
                ? preview.decisions.map((item) => (
                    <li key={item.text}>
                      {item.text} <span>{item.marker}</span>
                    </li>
                  ))
                : <li>None confirmed yet.</li>}
            </ul>
            <h3>Success</h3>
            <ul>
              {preview.success?.length
                ? preview.success.map((item) => <li key={item}>{item}</li>)
                : <li>{glance.success}</li>}
            </ul>
            <h3>Open items</h3>
            <ul>
              {preview.openItems.length ? preview.openItems.map((item) => <li key={item}>{item}</li>) : <li>None.</li>}
            </ul>
          </div>
        </aside>
        )}
      </div>
    </div>
  );
}
